import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { AppState } from 'src/store/store';
import guidesApiService from 'src/api/services/guidesApiService';
import { AirlinesState } from './types';
import { notification } from 'antd';
import dayjs from 'dayjs';

const initialState: AirlinesState = {
    isLoading: false,
    items: [],
    filter: {}
};

export const loadAirlinesAsync = createAsyncThunk('airlines/load', async () => {
    try {
        const data = await guidesApiService.getAirlines();
        return data;
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
            throw error;
        }
        throw new Error();
    }
});

const slice = createSlice({
    name: 'airlines',
    initialState,
    reducers: {
        reset: () => initialState,
        nameFilterChanged: (state, action: PayloadAction<string>) => {
            state.filter.name = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadAirlinesAsync.pending, (state) => {
                state.isLoading = true;
                state.items = [];
            })
            .addCase(loadAirlinesAsync.fulfilled, (state, action) => {
                state.isLoading = false;
                state.items = action.payload.map((dto) => ({
                    ...dto,
                    periods: dto.periods.map((periodDto) => ({
                        start: dayjs(periodDto.start).format('DD.MM.YYYY'),
                        end: dayjs(periodDto.end).format('DD.MM.YYYY')
                    }))
                }));
            })
            .addCase(loadAirlinesAsync.rejected, (state) => {
                state.isLoading = false;
            });
    }
});

export const { reset, nameFilterChanged } = slice.actions;

const selectAirlines = (state: AppState) => state.airlines;

const selectItems = (state: AppState) => selectAirlines(state).items;

export const selectFilter = (state: AppState) => selectAirlines(state).filter;

export const selectFilteredItems = createSelector([selectItems, selectFilter], (items, filter) => {
    let result = items;
    if (filter.name) {
        result = result.filter((item) => item.name.toLowerCase().includes(filter.name!.toLowerCase()));
    }
    return result;
});

export const selectTotalCount = createSelector(selectFilteredItems, (items) => items.length);

export const selectAirlinesLoading = (state: AppState) => selectAirlines(state).isLoading;

export default slice.reducer;
